import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { WebapiService } from '../webapi.service';

export interface UploadResult {
  fileName: string;
  empId: string;
  status: 'pending' | 'uploading' | 'success' | 'failed';
  appStatus: string;
  reportLink: string;
  errorMsg: string;
}

@Component({
  selector: 'app-new-bulk-upload',
  templateUrl: './new-bulk-upload.component.html',
  styleUrls: ['./new-bulk-upload.component.scss']
})
export class NewBulkUploadComponent implements OnInit {

  @ViewChild('uploadResultsSection') uploadResultsSection!: ElementRef;

  selectedFiles: FileList | null = null;
  isBulkUploading: boolean = false;
  bulkCurrentIndex: number = 0;
  bulkTotalFiles: number = 0;
  uploadResults: UploadResult[] = [];

  reportUrl: string = '';
  imageUrl: string = '';

  constructor(private WebApiService: WebapiService) {
    this.reportUrl = this.WebApiService.reportUrl;
    this.imageUrl = this.WebApiService.imageUrl;
  }

  ngOnInit(): void { }

  get progressPercent(): number {
    if (this.bulkTotalFiles === 0) return 0;
    return Math.round((this.bulkCurrentIndex / this.bulkTotalFiles) * 100);
  }

  get successCount(): number {
    return this.uploadResults.filter(r => r.status === 'success').length;
  }

  get failedCount(): number {
    return this.uploadResults.filter(r => r.status === 'failed').length;
  }

  onFileSelect(event: any): void {
    const files: FileList = event.target.files;

    if (files.length === 0) return;

    if (files.length > 40) {
      alert('You can only upload a maximum of 40 files at once.');
      event.target.value = '';
      this.selectedFiles = null;
      return;
    }

    // Reset results table on new file selection
    this.uploadResults = [];
    this.bulkCurrentIndex = 0;
    this.bulkTotalFiles = 0;
    this.selectedFiles = files;

    // Pre-populate results table with pending rows
    for (let i = 0; i < files.length; i++) {
      const empId = this.extractEmpId(files[i].name);
      this.uploadResults.push({
        fileName: files[i].name,
        empId: empId,
        status: 'pending',
        appStatus: '—',
        reportLink: '',
        errorMsg: ''
      });
    }
  }

  async startUpload(): Promise<void> {
    if (!this.selectedFiles || this.selectedFiles.length === 0) {
      alert('Please select files first.');
      return;
    }

    // Auto-scroll to Upload Results section
    if (this.uploadResultsSection) {
      this.uploadResultsSection.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    this.isBulkUploading = true;
    this.bulkTotalFiles = this.selectedFiles.length;
    this.bulkCurrentIndex = 0;

    for (let i = 0; i < this.selectedFiles.length; i++) {
      this.bulkCurrentIndex = i + 1;
      const file = this.selectedFiles[i];
      const empId = this.extractEmpId(file.name);

      // Mark current row as uploading
      this.uploadResults[i].status = 'uploading';

      try {
        const res: any = await this.uploadFileAsync(file, empId);
        console.log(`Upload Response for ${empId} (${file.name}):`, res);

        this.uploadResults[i].status = 'success';

        try {
          const appDetailsObj: any = await this.getAppDetailsAsync(empId);
          console.log(`App Details Response for ${empId}:`, appDetailsObj);

          this.uploadResults[i].appStatus = appDetailsObj?.status || '—';

          let fetchedLink = appDetailsObj?.link;
          if (fetchedLink && fetchedLink !== 'No link') {
            if (fetchedLink.startsWith('http://') || fetchedLink.startsWith('https://')) {
              this.uploadResults[i].reportLink = fetchedLink;
            } else {
              this.uploadResults[i].reportLink = this.reportUrl + fetchedLink;
            }
          } else {
            this.uploadResults[i].reportLink = '';
          }
        } catch (detailErr) {
          console.error(`Error fetching details for ${empId}`, detailErr);
          this.uploadResults[i].appStatus = res?.appStatus || res?.status || '—';
          const savedFile = res?.savedFileName || res?.fileName || res?.file || '';
          this.uploadResults[i].reportLink = savedFile ? this.reportUrl + savedFile : '';
        }

      } catch (err: any) {
        this.uploadResults[i].status = 'failed';
        this.uploadResults[i].errorMsg = err?.error?.message || err?.message || 'Upload failed';
        console.error(`Error uploading ${file.name}`, err);
      }
    }

    this.isBulkUploading = false;
  }

  private getAppDetailsAsync(empId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.WebApiService.getAppByEmpIdV2(empId).subscribe(
        (res: any) => resolve(res),
        (error: any) => reject(error)
      );
    });
  }

  private uploadFileAsync(file: File, empId: string): Promise<any> {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('empId', empId);
    formData.append('url', this.imageUrl);
    formData.append('type', 'return');

    return new Promise((resolve, reject) => {
      this.WebApiService.uploadMultipleImage(formData).subscribe(
        (res: any) => resolve(res),
        (error: any) => reject(error)
      );
    });
  }

  /**
   * Extract empId from filename stem — matches existing logic in application.component.ts:
   *   full stem is used (e.g. "RSM01245350-Final" from "RSM01245350-Final.pdf")
   */
  private extractEmpId(fileName: string): string {
    let stem = fileName.replace(/\.[^.]+$/, '');  // strip extension
    return stem.split('-')[0]; // Extract the exact empId before any dash
  }

  reset(): void {
    this.selectedFiles = null;
    this.uploadResults = [];
    this.bulkCurrentIndex = 0;
    this.bulkTotalFiles = 0;
    this.isBulkUploading = false;
    // clear the file input
    const input = document.getElementById('bulkFileInput') as HTMLInputElement;
    if (input) input.value = '';
  }
}
